import { useId, useState } from 'react';

import type { GreenCoffeeFieldKey, GreenCoffeeFields } from '@/hooks/useGreenCoffee';
import { countries, countryName } from '@/lib/countries';

import { ChevronDownIcon, CrossIcon } from './greenCoffeeIcons';
import { FIELD_LABELS } from './greenCoffeeStatus';

const INPUT =
  'box-border h-11 w-full min-w-0 rounded-lg bg-white px-3 text-[15px] text-black focus:outline-hidden lg:h-[34px] lg:px-2.5 lg:text-[13.5px]';

function borderFor(changed: boolean): string {
  return changed ? 'border-[1.5px] border-wolisho-brown' : 'border border-wolisho-brown/20';
}

function display(key: GreenCoffeeFieldKey, fields: GreenCoffeeFields): string {
  const value = fields[key];
  if (key === 'countryCode') return countryName(fields.countryCode) ?? '—';
  if (key === 'isBlend') return fields.isBlend ? 'Blend' : 'Single origin';
  if (Array.isArray(value)) return value.length > 0 ? value.join(', ') : '—';
  return value === null || value === '' ? '—' : String(value);
}

interface RowProps {
  field: GreenCoffeeFieldKey;
  htmlFor?: string;
  original: GreenCoffeeFields;
  changed: boolean;
  onUndo: () => void;
  children: React.ReactNode;
  last?: boolean;
}

function Row({ field, htmlFor, original, changed, onUndo, children, last = false }: RowProps) {
  const label = FIELD_LABELS[field];
  return (
    <div
      className={`flex flex-col gap-[5px] py-2 lg:flex-row lg:gap-2.5 lg:py-1.5 ${
        last ? '' : 'border-b border-wolisho-brown/[0.06]'
      }`}
    >
      {htmlFor ? (
        <label
          htmlFor={htmlFor}
          className={`text-[11.5px] font-bold lg:flex-[0_0_92px] lg:pt-[9px] ${
            changed ? 'text-wolisho-brown' : 'text-wolisho-brown/70'
          }`}
        >
          {label}
        </label>
      ) : (
        <span
          className={`text-[11.5px] font-bold lg:flex-[0_0_92px] lg:pt-[9px] ${
            changed ? 'text-wolisho-brown' : 'text-wolisho-brown/70'
          }`}
        >
          {label}
        </span>
      )}
      <div className="min-w-0 flex-1">
        {children}
        {changed && (
          <p
            className="mt-[5px] flex flex-wrap items-center gap-1.5 text-xs font-bold text-wolisho-brown/65 lg:text-[11.5px]"
            data-testid={`green-coffee-correct-changed-${field}`}
          >
            Changed · was {display(field, original)}
            <button
              type="button"
              onClick={onUndo}
              data-testid={`green-coffee-correct-undo-${field}`}
              className="border-0 bg-transparent p-0 font-bold text-wolisho-brown underline underline-offset-2"
            >
              Undo
            </button>
          </p>
        )}
      </div>
    </div>
  );
}

interface ChipListProps {
  id: string;
  testId: string;
  values: string[];
  original: string[];
  placeholder: string;
  changed: boolean;
  onChange: (values: string[]) => void;
}

function ChipList({ id, testId, values, original, placeholder, changed, onChange }: ChipListProps) {
  const [pending, setPending] = useState('');

  // Typed text counts once the input loses focus, so a note left unconfirmed
  // still reaches Publish corrected.
  const commit = () => {
    const value = pending.trim();
    setPending('');
    if (value !== '' && !values.includes(value)) onChange([...values, value]);
  };

  return (
    <div
      className={`box-border flex min-h-11 flex-wrap items-center gap-1.5 rounded-lg bg-white px-2 py-1.5 lg:min-h-[34px] lg:gap-[5px] lg:px-1.5 lg:py-1 ${borderFor(changed)}`}
    >
      {values.map((value, index) => {
        const added = !original.includes(value);
        return (
          <span
            // The normalizer can store duplicates, so a value alone is not a unique key.
            key={`${String(index)}-${value}`}
            className={`inline-flex items-center gap-0.5 rounded-md py-1 pl-[9px] pr-0.5 text-[13px] font-bold lg:py-[3px] lg:pl-2 lg:text-[12.5px] ${
              added
                ? 'bg-green-600/[0.12] text-green-800'
                : 'bg-wolisho-brown/[0.09] text-wolisho-brown'
            }`}
          >
            {value}
            <button
              type="button"
              aria-label={`Remove ${value}`}
              onClick={() => onChange(values.filter((_, i) => i !== index))}
              className="flex h-6 w-6 items-center justify-center border-0 bg-transparent p-0 opacity-70 lg:h-[18px] lg:w-[18px]"
            >
              <CrossIcon size={10} />
            </button>
          </span>
        );
      })}
      <input
        id={id}
        type="text"
        value={pending}
        placeholder={placeholder}
        data-testid={testId}
        onChange={(e) => setPending(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            commit();
          }
        }}
        className="min-w-20 flex-1 border-0 bg-transparent p-1 text-[15px] text-black focus:outline-hidden lg:p-0.5 lg:text-[13px]"
      />
    </div>
  );
}

interface Props {
  value: GreenCoffeeFields;
  original: GreenCoffeeFields;
  changed: readonly GreenCoffeeFieldKey[];
  onChange: (fields: GreenCoffeeFields) => void;
}

export function GreenCoffeeCorrectForm({ value, original, changed, onChange }: Props) {
  const idPrefix = useId();
  const id = (field: GreenCoffeeFieldKey) => `${idPrefix}-${field}`;
  const set = (patch: Partial<GreenCoffeeFields>) => onChange({ ...value, ...patch });
  const undo = (field: GreenCoffeeFieldKey) => () => set({ [field]: original[field] });
  const isChanged = (field: GreenCoffeeFieldKey) => changed.includes(field);

  const textRow = (field: 'name' | 'region' | 'farm' | 'processingType') => (
    <Row
      field={field}
      htmlFor={id(field)}
      original={original}
      changed={isChanged(field)}
      onUndo={undo(field)}
    >
      <input
        id={id(field)}
        type="text"
        value={value[field] ?? ''}
        onChange={(e) => set({ [field]: e.target.value })}
        data-testid={`green-coffee-correct-field-${field}`}
        className={`${INPUT} ${borderFor(isChanged(field))}`}
      />
    </Row>
  );

  return (
    <form
      onSubmit={(e) => e.preventDefault()}
      data-testid="green-coffee-correct-form"
      className="m-0 rounded-xl bg-wolisho-card px-4 pb-3 pt-1.5"
    >
      {textRow('name')}
      <Row
        field="countryCode"
        htmlFor={id('countryCode')}
        original={original}
        changed={isChanged('countryCode')}
        onUndo={undo('countryCode')}
      >
        <div className="relative">
          <select
            id={id('countryCode')}
            value={value.countryCode ?? ''}
            onChange={(e) => set({ countryCode: e.target.value === '' ? null : e.target.value })}
            data-testid="green-coffee-correct-field-countryCode"
            className={`${INPUT} appearance-none pr-[34px] lg:pr-[30px] ${borderFor(isChanged('countryCode'))}`}
          >
            <option value="">No country</option>
            {countries.map((country) => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-3 top-1/2 flex -translate-y-1/2 text-wolisho-brown/50 lg:right-2.5">
            <ChevronDownIcon />
          </span>
        </div>
      </Row>
      {textRow('region')}
      {textRow('farm')}
      {textRow('processingType')}
      {(['varieties', 'tastingNotes'] as const).map((field) => (
        <Row
          key={field}
          field={field}
          htmlFor={id(field)}
          original={original}
          changed={isChanged(field)}
          onUndo={undo(field)}
        >
          <ChipList
            id={id(field)}
            testId={`green-coffee-correct-field-${field}`}
            values={value[field]}
            original={original[field]}
            placeholder={field === 'varieties' ? 'Add variety' : 'Add note'}
            changed={isChanged(field)}
            onChange={(values) => set({ [field]: values })}
          />
        </Row>
      ))}
      <Row
        field="isBlend"
        original={original}
        changed={isChanged('isBlend')}
        onUndo={undo('isBlend')}
        last
      >
        <div
          role="group"
          aria-label="Blend"
          className="inline-flex gap-1 rounded-lg bg-wolisho-brown/[0.06] p-[3px]"
        >
          {[
            { label: 'Blend', blend: true },
            { label: 'Single origin', blend: false },
          ].map((option) => {
            const active = value.isBlend === option.blend;
            return (
              <button
                key={option.label}
                type="button"
                aria-pressed={active}
                onClick={() => set({ isBlend: option.blend })}
                data-testid={`green-coffee-correct-blend-${option.blend ? 'yes' : 'no'}`}
                className={`min-h-[38px] rounded-md border-0 px-3.5 text-[13px] font-bold lg:min-h-0 lg:px-3 lg:py-1.5 lg:text-[12.5px] ${
                  active ? 'bg-white text-black' : 'bg-transparent text-wolisho-brown/60'
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </Row>
    </form>
  );
}
